import { useState } from "react";
import { styled, useMediaQuery } from "@mui/material";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { CustomText } from "../../../styles/CustomText";
import {
  GRAYPALEBACKGROUND1,
  GRAYPALEBORDER,
  GRAYPALECONTENTS,
  GRAYPALEDISABLED,
  GRAYPALETITLE,
} from "../../../styles/color";
import { useSasToken } from "../../../hooks/useSasToken";
import ControlHints from "./ControlHints";
import PhotoLightbox, { ThumbStrip } from "./PhotoLightbox";
import { stepIndex } from "./zoomModel";
import { PhotoTimes, photoTimeLabel } from "./photoTime";

export interface GalleryPhoto extends PhotoTimes {
  name: string;
  /** SAS 를 붙이지 않은 저장 주소 */
  url: string;
}

interface PhotoGalleryProps {
  /** 사진 묶음 이름 (예: "사용 후 보고 사진"). 크게 보기 제목에도 쓴다 */
  title: string;
  photos: GalleryPhoto[];
  emptyText?: string;
}

/**
 * 사진 여러 장을 격자로 보여주고, 누르면 크게 보기로 넘겨 본다. 휴양소 입실·퇴실 사진이 쓴다.
 * 크게 보기의 확대·이동·넘기기·뒤로가기 닫기는 차량 사진과 같은 PhotoLightbox 를 쓴다.
 */
function PhotoGallery({ title, photos, emptyText = "등록된 사진이 없습니다" }: PhotoGalleryProps) {
  const { appendSas } = useSasToken();
  const isMobile = useMediaQuery("(max-width:620px)");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const current = openIndex !== null ? photos[openIndex] : undefined;
  const step = (delta: number) =>
    setOpenIndex((index) => (index === null ? index : stepIndex(index, delta, photos.length)));

  return (
    <Root>
      <Head>
        <CustomText type="subtitle" bold500 color={GRAYPALETITLE}>
          {title}
        </CustomText>
        <CustomText type="subtitle" color={GRAYPALECONTENTS}>
          {photos.length}장
        </CustomText>
      </Head>

      {photos.length === 0 ? (
        <Empty>
          <PhotoLibraryOutlinedIcon sx={{ fontSize: 40, color: GRAYPALEDISABLED }} />
          <CustomText type="subtitle" bold500 color={GRAYPALECONTENTS}>
            {emptyText}
          </CustomText>
        </Empty>
      ) : (
        <>
          <Grid>
            {photos.map((photo, index) => (
              <Thumb
                key={`${photo.url}-${index}`}
                type="button"
                onClick={() => setOpenIndex(index)}
                aria-label={`${title} ${index + 1}번째 크게 보기`}
              >
                <img src={appendSas(photo.url)} alt="" loading="lazy" />
                <Badge>{index + 1}</Badge>
                {photoTimeLabel(photo, false) && <TimeTag>{photoTimeLabel(photo, false)}</TimeTag>}
              </Thumb>
            ))}
          </Grid>
          <HintWrap>
            <ControlHints
              items={
                isMobile
                  ? [{ icon: <TouchAppOutlinedIcon />, label: "사진을 누르면 크게 보기" }]
                  : [{ keys: ["클릭"], label: "사진 크게 보기" }]
              }
            />
          </HintWrap>
        </>
      )}

      {current && openIndex !== null && (
        <PhotoLightbox
          title={title}
          subtitle={photoTimeLabel(current, true) || undefined}
          imageUrl={appendSas(current.url)}
          downloadName={current.name}
          isMobile={isMobile}
          onPrev={photos.length > 1 ? () => step(-1) : undefined}
          onNext={photos.length > 1 ? () => step(1) : undefined}
          onClose={() => setOpenIndex(null)}
          count={`${openIndex + 1} / ${photos.length}`}
          strip={
            photos.length > 1 && (
              <ThumbStrip
                items={photos.map((photo, index) => ({
                  key: `${photo.url}-${index}`,
                  url: appendSas(photo.url),
                  label: `${title} ${index + 1}번째`,
                }))}
                selectedIndex={openIndex}
                onSelect={setOpenIndex}
              />
            )
          }
        />
      )}
    </Root>
  );
}

export default PhotoGallery;

const Root = styled("div")({
  width: "100%",
  margin: "16px 0",
});

const Head = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px",
});

const Grid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: "10px",
});

// 4:3 칸. aspect-ratio 를 못 쓰는 구형 브라우저를 위해 padding 으로 높이를 잡는다.
const Thumb = styled("button")({
  position: "relative",
  width: "100%",
  paddingTop: "75%",
  border: `1px solid ${GRAYPALEBORDER}`,
  borderRadius: "8px",
  overflow: "hidden",
  cursor: "zoom-in",
  backgroundColor: GRAYPALEBACKGROUND1,
  "& img": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  "&:hover img": { opacity: 0.9 },
});

const Badge = styled("span")({
  position: "absolute",
  top: "6px",
  left: "6px",
  minWidth: "22px",
  padding: "1px 7px",
  borderRadius: "10px",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#fff",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
});

const TimeTag = styled("span")({
  position: "absolute",
  right: "6px",
  bottom: "6px",
  padding: "1px 7px",
  borderRadius: "10px",
  fontSize: "0.72rem",
  color: "#fff",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
});

const Empty = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "180px",
  borderRadius: "8px",
  border: `1px dashed ${GRAYPALEBORDER}`,
  backgroundColor: GRAYPALEBACKGROUND1,
});

const HintWrap = styled("div")({
  marginTop: "10px",
});
